import React, { useState } from 'react';
import VoiceIntegrityModal from './VoiceIntegrityModal';
import './VoiceComplianceCards.css';

const VoiceIntegrityCard = ({ userId, voiceIntegrityData, onUpdate, disabled = false, isProfileApproved = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasVoiceIntegrity = voiceIntegrityData && voiceIntegrityData.id;

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { badge: 'draft', label: 'Draft' },
      'pending-review': { badge: 'pending', label: 'Pending Review' },
      'twilio-approved': { badge: 'approved', label: 'Approved' },
      'approved': { badge: 'approved', label: 'Approved' },
      'twilio-rejected': { badge: 'rejected', label: 'Rejected' },
      'failed': { badge: 'rejected', label: 'Failed' },
    };
    return statusMap[status] || { badge: 'draft', label: status };
  };

  const getHeaderColorClass = (status) => {
    const normalizedStatus = status ? status.toLowerCase() : '';
    if (['twilio-approved', 'approved'].includes(normalizedStatus)) return 'card-header--approved';
    if (['pending-review'].includes(normalizedStatus)) return 'card-header--pending';
    if (['twilio-rejected', 'rejected', 'failed'].includes(normalizedStatus)) return 'card-header--rejected';
    if (['draft'].includes(normalizedStatus)) return 'card-header--draft';
    return 'card-header--default';
  };

  const handleModalClose = async (updated) => {
    setShowModal(false);
    if (updated) {
      setIsRefreshing(true);
      await onUpdate();
      // Wait a bit for data to update
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1500);
    }
  };

  return (
    <>
      <div className={`voice-compliance-card ${disabled ? 'voice-compliance-card--disabled' : ''}`} id="VoiceIntegrityCard">
        {/* Header with Icon and Title */}
        <div className={`card-header ${hasVoiceIntegrity ? getHeaderColorClass(voiceIntegrityData.status) : ''}`}>
          <div className="card-icon-title">
            <i className="la la-phone-volume" style={{ fontSize: '20px' }}></i>
            <h3 className="card-title">Voice Integrity</h3>
          </div>
          {hasVoiceIntegrity && (
            <span className="status-text">{getStatusBadge(voiceIntegrityData.status).label}</span>
          )}
        </div>

        {/* No VI record — show Enable button only when profile AND STIR/SHAKEN are approved */}
        {!hasVoiceIntegrity && (
          !disabled ? (
            <button
              className="btn linear-gradient zy-save-btn"
              onClick={() => setShowModal(true)}
            >
              Enable Voice Integrity
            </button>
          ) : (
            <div className="status-message status-message-warning">
              <p>Complete Business Profile and STIR/SHAKEN first to enable Voice Integrity</p>
            </div>
          )
        )}

        {/* VI record exists — show status only, no button */}
        {hasVoiceIntegrity && (
          <>
            {voiceIntegrityData.status === 'twilio-approved' && (
              <div className="status-message status-message-success">
                <p>Voice Integrity approved - spam prevention active</p>
              </div>
            )}
            {voiceIntegrityData.status === 'pending-review' && (
              <div className="status-message status-message-warning">
                <p>Auto-configuration in progress (typically 24-48 hours)</p>
              </div>
            )}
            {voiceIntegrityData.status === 'twilio-rejected' && (
              <div className="status-message status-message-error">
                <p>Registration rejected.</p>
                <button
                  className="btn linear-gradient zy-save-btn"
                  onClick={() => setShowModal(true)}
                >
                  Resubmit
                </button>
              </div>
            )}
            {voiceIntegrityData.status === 'failed' && (
              <div className="status-message status-message-error">
                <p>Submission failed. Please try again.</p>
                <button
                  className="btn linear-gradient zy-save-btn"
                  onClick={() => setShowModal(true)}
                >
                  Resubmit
                </button>
              </div>
            )}
            {voiceIntegrityData.status === 'draft' && (
              <div className="status-message status-message-warning">
                <p>Configuration in progress</p>
              </div>
            )}
          </>
        )}

        {/* Refreshing Overlay */}
        {isRefreshing && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            borderRadius: '12px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '10px' }}></div>
              <p style={{ color: '#087ab6', fontSize: '13px', fontWeight: 500, margin: 0 }}>
                Refreshing data...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Voice Integrity Modal */}
      {showModal && (
        <VoiceIntegrityModal
          userId={userId}
          voiceIntegrityData={voiceIntegrityData}
          isEditMode={hasVoiceIntegrity}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default VoiceIntegrityCard;
